'use client';

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { BarChart2, FileDown, Calendar, Building2, Filter, Wifi,  } from 'lucide-react';
import { CLIENTS, INTERVENTIONS, type ConnectionType } from '@/lib/data';
import ReportKPICards from './ReportKPICards';
import ReportTypeChart from './ReportTypeChart';
import ReportDailyChart from './ReportDailyChart';
import ReportTable from './ReportTable';

function pad(n: number) { return String(n).padStart(2, '0'); }

function firstOfMonth() {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-01`;
}
function today() {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
}

export default function ReportsScreen() {
  const [filterClient, setFilterClient] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(firstOfMonth());
  const [filterDateTo, setFilterDateTo] = useState(today());
  const [filterType, setFilterType] = useState<ConnectionType | ''>('');
  const [generating, setGenerating] = useState(false);

  const filtered = useMemo(() => {
    return INTERVENTIONS.filter(i => {
      if (filterClient && i.clientId !== filterClient) return false;
      if (i.date < filterDateFrom || i.date > filterDateTo) return false;
      if (filterType && i.connectionType !== filterType) return false;
      return true;
    }).sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  }, [filterClient, filterDateFrom, filterDateTo, filterType]);

  const totalDuration = filtered.reduce((s, i) => s + i.duration, 0);
  const uniqueClients = new Set(filtered.map(i => i.clientId)).size;
  const completedCount = filtered.filter(i => i.status === 'Završena').length;

  // Type breakdown for chart
  const typeBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    filtered.forEach(i => {
      map[i.connectionType] = (map[i.connectionType] || 0) + 1;
    });
    return Object.entries(map)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  // Daily breakdown for area chart
  const dailyBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    filtered.forEach(i => {
      map[i.date] = (map[i.date] || 0) + 1;
    });
    // Build all days in range
    const days: { date: string; label: string; count: number }[] = [];
    const from = new Date(filterDateFrom);
    const to = new Date(filterDateTo);
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const dd = pad(d.getDate());
      const mm = pad(d.getMonth() + 1);
      days.push({ date: key, label: `${dd}.${mm}`, count: map[key] || 0 });
    }
    return days;
  }, [filtered, filterDateFrom, filterDateTo]);

  // Client breakdown
  const clientBreakdown = useMemo(() => {
    const map: Record<string, { clientId: string; clientName: string; count: number; totalMin: number }> = {};
    filtered.forEach(i => {
      if (!map[i.clientId]) {
        map[i.clientId] = { clientId: i.clientId, clientName: i.clientName, count: 0, totalMin: 0 };
      }
      map[i.clientId].count++;
      map[i.clientId].totalMin += i.duration;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [filtered]);

  function formatDate(d: string) {
    const [y, m, day] = d.split('-');
    return `${day}.${m}.${y}`;
  }

  function formatDuration(min: number) {
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }

  // PDF export — uses print window approach
  // Backend integration: POST /api/reports/pdf with filtered intervention IDs
  function handleExportPDF() {
    if (filtered.length === 0) {
      toast.error('Nema intervencija za izabrani period i klijenta.');
      return;
    }
    setGenerating(true);
    const clientName = filterClient
      ? CLIENTS.find(c => c.id === filterClient)?.name || 'Svi klijenti' :'Svi klijenti';

    setTimeout(() => {
      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        toast.error('Preglednik je blokirao otvaranje prozora. Dozvolite popup prozore.');
        setGenerating(false);
        return;
      }

      const rows = filtered.map(inv => {
        const [y, m, d] = inv.date.split('-');
        return `
          <tr>
            <td>${d}.${m}.${y} ${inv.time}</td>
            <td>${inv.clientName}</td>
            <td>${inv.connectionType}</td>
            <td>${inv.description}</td>
            <td>${inv.duration} min</td>
            <td>${inv.status}</td>
          </tr>
        `;
      }).join('');

      const typeRows = typeBreakdown.map(t =>
        `<tr><td>${t.type}</td><td>${t.count}</td></tr>`
      ).join('');

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="sr">
        <head>
          <meta charset="UTF-8">
          <title>ServisPro — Izveštaj intervencija</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400&display=swap');
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'DM Sans', sans-serif; color: #1a2332; background: white; padding: 32px; font-size: 13px; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; padding-bottom: 16px; border-bottom: 2px solid #1e3a5f; }
            .logo { font-size: 22px; font-weight: 700; color: #1e3a5f; }
            .logo span { color: #2563eb; }
            .meta { text-align: right; color: #64748b; font-size: 12px; line-height: 1.6; }
            h2 { font-size: 16px; font-weight: 600; color: #1e3a5f; margin: 20px 0 10px; }
            .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
            .kpi { background: #f1f5f9; border-radius: 8px; padding: 12px 16px; }
            .kpi-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; font-weight: 600; }
            .kpi-value { font-size: 22px; font-weight: 700; color: #1e3a5f; margin-top: 4px; font-variant-numeric: tabular-nums; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { background: #1e3a5f; color: white; padding: 8px 10px; text-align: left; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
            td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
            tr:nth-child(even) td { background: #f8fafc; }
            .desc { max-width: 280px; line-height: 1.4; }
            .summary-table { width: auto; min-width: 300px; }
            .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 11px; display: flex; justify-content: space-between; }
            @media print { body { padding: 16px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="logo">Servis<span>Pro</span></div>
              <div style="color:#64748b;font-size:12px;margin-top:4px;">IT Servisna evidencija</div>
            </div>
            <div class="meta">
              <strong>Izveštaj intervencija</strong><br>
              Klijent: ${clientName}<br>
              Period: ${formatDate(filterDateFrom)} — ${formatDate(filterDateTo)}<br>
              Generisano: ${formatDate(today())}
            </div>
          </div>

          <div class="kpi-grid">
            <div class="kpi">
              <div class="kpi-label">Ukupno intervencija</div>
              <div class="kpi-value">${filtered.length}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Ukupno vreme</div>
              <div class="kpi-value">${formatDuration(totalDuration)}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Klijenata</div>
              <div class="kpi-value">${uniqueClients}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Završenih</div>
              <div class="kpi-value">${completedCount}</div>
            </div>
          </div>

          <h2>Pregled po tipu veze</h2>
          <table class="summary-table" style="margin-bottom:24px;">
            <thead><tr><th>Tip veze</th><th>Broj intervencija</th></tr></thead>
            <tbody>${typeRows}</tbody>
          </table>

          <h2>Spisak intervencija</h2>
          <table>
            <thead>
              <tr>
                <th>Datum i vreme</th>
                <th>Klijent</th>
                <th>Tip veze</th>
                <th>Opis</th>
                <th>Trajanje</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>

          <div class="footer">
            <span>ServisPro — IT Servisna evidencija</span>
            <span>Strana 1 od 1 · ${formatDate(today())}</span>
          </div>

          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
      setGenerating(false);
      toast.success('PDF izveštaj je generisan i otvoren za štampu.');
    }, 800);
  }

  const ALL_CONN_TYPES: ConnectionType[] = ['Na lokaciji', 'VPN', 'VNC', 'RDP', 'AnyDesk', 'TeamViewer'];

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: 'hsl(var(--background))' }}>
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-5 border-b sticky top-0 z-10"
        style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}
      >
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
            Izveštaji
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
            Pregled intervencija po klijentu i periodu · izvoz u PDF
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={handleExportPDF}
          disabled={generating || filtered.length === 0}
        >
          {generating ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
              </svg>
              Generisanje...
            </span>
          ) : (
            <>
              <FileDown size={16} />
              Izvoz u PDF
            </>
          )}
        </button>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Filter sidebar */}
        <div
          className="w-72 min-w-[288px] border-r flex-shrink-0 p-5 space-y-5 overflow-y-auto scrollbar-thin"
          style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}
        >
          <div className="flex items-center gap-2">
            <Filter size={16} style={{ color: 'hsl(var(--accent))' }} />
            <h2 className="font-semibold text-sm" style={{ color: 'hsl(var(--foreground))' }}>
              Parametri izveštaja
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">
                <Building2 size={13} className="inline mr-1" />
                Klijent
              </label>
              <select
                className="input-field text-sm"
                value={filterClient}
                onChange={e => setFilterClient(e.target.value)}
              >
                <option value="">Svi klijenti</option>
                {CLIENTS.map(c => (
                  <option key={`rep-client-${c.id}`} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="helper-text">Ostavite prazno za sve klijente</p>
            </div>

            <div>
              <label className="label">
                <Calendar size={13} className="inline mr-1" />
                Period od
              </label>
              <input
                type="date"
                className="input-field text-sm"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
              />
            </div>

            <div>
              <label className="label">
                <Calendar size={13} className="inline mr-1" />
                Period do
              </label>
              <input
                type="date"
                className="input-field text-sm"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
              />
            </div>

            <div>
              <label className="label">
                <Wifi size={13} className="inline mr-1" />
                Tip veze
              </label>
              <select
                className="input-field text-sm"
                value={filterType}
                onChange={e => setFilterType(e.target.value as ConnectionType | '')}
              >
                <option value="">Svi tipovi</option>
                {ALL_CONN_TYPES.map(t => (
                  <option key={`rep-type-${t}`} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick date presets */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'hsl(var(--muted-foreground))', letterSpacing: '0.07em' }}>
              Brzi period
            </p>
            <div className="space-y-1">
              {[
                { label: 'Ovaj mesec', fn: () => { setFilterDateFrom(firstOfMonth()); setFilterDateTo(today()); } },
                { label: 'Prethodnih 30 dana', fn: () => {
                  const n = new Date();
                  n.setDate(n.getDate() - 30);
                  setFilterDateFrom(`${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`);
                  setFilterDateTo(today());
                }},
                { label: 'Prethodnih 90 dana', fn: () => {
                  const n = new Date();
                  n.setDate(n.getDate() - 90);
                  setFilterDateFrom(`${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`);
                  setFilterDateTo(today());
                }},
                { label: 'Ova godina', fn: () => {
                  const y = new Date().getFullYear();
                  setFilterDateFrom(`${y}-01-01`);
                  setFilterDateTo(today());
                }},
              ].map(preset => (
                <button
                  key={`preset-${preset.label}`}
                  onClick={preset.fn}
                  className="w-full text-left text-sm px-3 py-2 rounded-lg transition-colors duration-100"
                  style={{ color: 'hsl(var(--muted-foreground))' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'hsl(var(--muted))')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Summary box */}
          <div
            className="p-4 rounded-xl border"
            style={{ backgroundColor: 'hsl(217 91% 60% / 0.06)', borderColor: 'hsl(217 91% 60% / 0.2)' }}
          >
            <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'hsl(var(--accent))', letterSpacing: '0.07em' }}>
              Rezultat filtera
            </p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>Intervencija</span>
                <span className="font-bold tabular-nums" style={{ color: 'hsl(var(--foreground))' }}>{filtered.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>Ukupno vreme</span>
                <span className="font-bold tabular-nums" style={{ color: 'hsl(var(--foreground))' }}>{formatDuration(totalDuration)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>Klijenata</span>
                <span className="font-bold tabular-nums" style={{ color: 'hsl(var(--foreground))' }}>{uniqueClients}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main report content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-6 max-w-screen-2xl">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <BarChart2 size={48} style={{ color: 'hsl(var(--muted-foreground))' }} />
              <p className="text-lg font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                Nema intervencija za izabrani period
              </p>
              <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
                Promenite parametre filtera u levom panelu da biste videli podatke.
              </p>
            </div>
          ) : (
            <>
              {/* KPI row */}
              <ReportKPICards
                total={filtered.length}
                totalDuration={totalDuration}
                uniqueClients={uniqueClients}
                completedCount={completedCount}
                formatDuration={formatDuration}
              />

              {/* Charts row */}
              <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-2 gap-6">
                <ReportTypeChart data={typeBreakdown} />
                <ReportDailyChart data={dailyBreakdown} />
              </div>

              {/* Client breakdown */}
              {clientBreakdown.length > 1 && (
                <div className="card p-5">
                  <h3 className="font-semibold text-base mb-4" style={{ color: 'hsl(var(--foreground))' }}>
                    Pregled po klijentu
                  </h3>
                  <div className="space-y-3">
                    {clientBreakdown.map(cb => {
                      const pct = Math.round((cb.count / filtered.length) * 100);
                      return (
                        <div key={`cb-${cb.clientId}`} className="flex items-center gap-4">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: 'hsl(var(--accent) / 0.12)', color: 'hsl(var(--accent))' }}
                          >
                            {cb.clientName.slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>
                                {cb.clientName}
                              </span>
                              <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                                <span className="text-xs tabular-nums" style={{ color: 'hsl(var(--muted-foreground))' }}>
                                  {formatDuration(cb.totalMin)}
                                </span>
                                <span
                                  className="text-xs font-bold tabular-nums w-10 text-right"
                                  style={{ color: 'hsl(var(--accent))' }}
                                >
                                  {cb.count}×
                                </span>
                              </div>
                            </div>
                            <div
                              className="h-1.5 rounded-full overflow-hidden"
                              style={{ backgroundColor: 'hsl(var(--muted))' }}
                            >
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${pct}%`, backgroundColor: 'hsl(var(--accent))' }}
                              />
                            </div>
                          </div>
                          <span
                            className="text-xs font-semibold tabular-nums w-10 text-right flex-shrink-0"
                            style={{ color: 'hsl(var(--muted-foreground))' }}
                          >
                            {pct}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Interventions table */}
              <ReportTable
                interventions={filtered}
                formatDate={formatDate}
                formatDuration={formatDuration}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}